/**
 * Manutenção › Monitoramento.
 *
 * 🔴 UM ÚNICO item de menu, com duas abas dentro: `Ao vivo` (abre primeiro) e
 * ⚠️ **A aba `Indicadores` MIGROU para `Manutenção › Indicadores` em 04/09/2026**
 * — decisão do Matheus, para separar fila de trabalho de visão de gestão. Aqui
 * ficou só **Ao vivo**, que é o que esta tela é: o agora. 🔴 Ela não foi
 * duplicada; dois lugares com o mesmo nome no mesmo menu seriam o "segundo
 * motor" em forma de tela.
 *
 * Decisão do Matheus em 01/09 — não existe item de menu
 * separado para cada uma.
 *
 * 🔴 A regra que atravessa a tela inteira: **"sem informação" nunca aparece
 * somado com "fora do ar"**. Nem no card, nem na tabela, nem na cor. Se a
 * internet de um cliente cai, os equipamentos dele podem estar perfeitos —
 * somar produziria um número falso que manda a equipe procurar defeito onde
 * não há.
 */
import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '@/lib/api';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  HelpTip,
  Input,
  KpiCard,
  Label,
  PageContainer,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ThOrdenavel,
  ordenarLista,
  useOrdenacao,
  type Ordenacao,
} from '@/components/ui';
import {
  CORES_ESTADO,
  desdeQuando,
  useAoVivo,
  type ClienteAoVivo,
  type EstadoMonitoramento,
} from '@/lib/monitoramento-store';
import { useRelogioVivo } from '@/lib/relogio-vivo';

const ESTADOS: Array<{ valor: EstadoMonitoramento; rotulo: string }> = [
  { valor: 'no_ar', rotulo: 'No ar' },
  { valor: 'fora_do_ar', rotulo: 'Fora do ar' },
  { valor: 'sem_informacao', rotulo: 'Sem informação' },
  { valor: 'silenciado', rotulo: 'Silenciado' },
];

type EquipamentoDaLista = ClienteAoVivo['equipamentos'][number];
type ChaveDaOrdem = 'nome' | 'cliente' | 'endereco' | 'estado' | 'desde' | 'teste';

/** Ordenar por "Estado" é por GRAVIDADE (problema primeiro), não por alfabeto. */
const RANQUE_ESTADO: Record<string, number> = {
  fora_do_ar: 0,
  sem_informacao: 1,
  silenciado: 2,
  no_ar: 3,
};

function valorDaOrdem(e: EquipamentoDaLista, chave: ChaveDaOrdem): string | number | null {
  switch (chave) {
    case 'nome':
      return e.nome;
    case 'endereco':
      return e.endereco;
    case 'estado':
      return RANQUE_ESTADO[e.estado] ?? 9;
    case 'desde':
      return e.estado === 'no_ar' ? null : e.desdeIso;
    case 'teste':
      return e.ultimoTesteIso;
    default:
      return null; // 'cliente' se resolve fora — o equipamento não sabe de quem é.
  }
}

function SeloDoEstado({ estado, rotulo }: { estado: EstadoMonitoramento; rotulo: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${CORES_ESTADO[estado]}`}
      data-testid={`estado-${estado}`}
    >
      {rotulo}
    </span>
  );
}

export default function MonitoramentoPage() {
  // ⚠️ A aba viva na query string, não em localStorage: é estado transiente e
  // o link fica compartilhável.
  const [params, setParams] = useSearchParams();
  const aba = params.get('aba') === 'indicadores' ? 'indicadores' : 'ao-vivo';

  return (
    <PageContainer variante="full">
      <PageHeader
        crumb="Manutenção"
        title="Monitoramento"
        subtitle="Um lugar só para o monitoramento: o que está acontecendo agora, e o que os números dizem sobre o parque ao longo do tempo."
      />
      <Tabs
        value={aba}
        onValueChange={(v) => {
          const p = new URLSearchParams(params);
          p.set('aba', v);
          setParams(p, { replace: true });
        }}
      >
        <TabsList seletorNoCelular="Monitoramento">
          <TabsTrigger value="ao-vivo">Ao vivo</TabsTrigger>
        </TabsList>
        <TabsContent value="ao-vivo">
          <AbaAoVivo ativa={aba === 'ao-vivo'} />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Ao vivo
// ═══════════════════════════════════════════════════════════════════

function AbaAoVivo({ ativa }: { ativa: boolean }) {
  const agora = useRelogioVivo();
  const { data, isLoading, error, dataUpdatedAt } = useAoVivo(ativa);
  const [cliente, setCliente] = useState('');
  const [busca, setBusca] = useState('');
  const [estados, setEstados] = useState<EstadoMonitoramento[]>([]);
  const [agrupar, setAgrupar] = useState(true);
  const ordem = useOrdenacao<ChaveDaOrdem>('nome');

  // ⚠️ `?? []` cria um array novo a cada render — sem o useMemo, o filtro
  // abaixo recalcularia sempre, inclusive nas atualizações de 30 em 30s.
  const clientes = useMemo(() => data?.clientes ?? [], [data?.clientes]);
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return clientes
      .filter((c) => !cliente || c.clienteId === cliente)
      .map((c) => ({
        ...c,
        equipamentos: c.equipamentos.filter(
          (e) =>
            (estados.length === 0 || estados.includes(e.estado)) &&
            (!termo || e.nome.toLowerCase().includes(termo) || e.endereco.toLowerCase().includes(termo)),
        ),
      }))
      .filter((c) => c.equipamentos.length > 0 || (!termo && estados.length === 0));
  }, [clientes, cliente, busca, estados]);

  // Lista corrida ordena o PARQUE INTEIRO de uma vez (inclusive por cliente);
  // agrupada, cada bloco de cliente se ordena por dentro, lá no LinhasDoCliente.
  const linhasCorridas = useMemo(() => {
    if (agrupar) return [];
    const todas = filtrados.flatMap((c) =>
      c.equipamentos.map((e) => ({ e, razaoSocial: c.razaoSocial })),
    );
    return ordenarLista(
      todas,
      (l) => (ordem.chave === 'cliente' ? l.razaoSocial : valorDaOrdem(l.e, ordem.chave)),
      ordem.sentido,
    );
  }, [filtrados, agrupar, ordem.chave, ordem.sentido]);

  if (error) {
    // ⚠️ 403 NÃO é falha de carregamento. Mandar recarregar quem não tem
    // permissão é um beco sem saída: por mais que recarregue, o servidor vai
    // responder a mesma coisa. A frase precisa dizer o que pedir a quem.
    const semPermissao = error instanceof ApiError && error.status === 403;
    return (
      <Alert variant={semPermissao ? 'warning' : 'danger'} data-testid="monitoramento-erro">
        {semPermissao ? (
          <>
            Você não tem acesso ao monitoramento. Peça a um administrador a permissão{' '}
            <strong>Monitoramento — acompanhar</strong>, na tela de Permissões.
          </>
        ) : (
          <>
            Não foi possível carregar o monitoramento agora. O estado que você vê pode estar velho —
            recarregue a página antes de tomar qualquer decisão a partir dele.
          </>
        )}
      </Alert>
    );
  }

  const t = data?.totais;
  const semColetor = clientes.length === 0 && !isLoading;

  return (
    <div className="space-y-6">
      {/* 🔴 O bloco que impede o painel de mentir. Sem ele, tudo abaixo pode
          estar verde sobre um parque que o sistema não enxerga. */}
      {semColetor ? (
        <EmptyState
          title="Nenhum cliente com coletor instalado"
          description="O monitoramento só enxerga clientes que têm coletor. Abra a ficha de um cliente, vá na aba Monitoramento e instale o primeiro."
          data-testid="monitoramento-vazio"
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              label="No ar"
              value={isLoading ? '—' : t!.noAr}
              context="responderam ao último teste"
              data-testid="kpi-no-ar"
            />
            <KpiCard
              label="Fora do ar"
              value={isLoading ? '—' : t!.foraDoAr}
              context="exigem ação"
              data-testid="kpi-fora-do-ar"
            />
            {/* 🔴 Card PRÓPRIO, nunca somado ao de cima. */}
            <KpiCard
              label="Sem informação"
              help={`Equipamentos de clientes cujo coletor parou de reportar. Podem estar funcionando — não sabemos. Este número nunca é somado ao de “fora do ar”.`}
              value={isLoading ? '—' : t!.semInformacao}
              context={
                isLoading
                  ? 'estado desconhecido'
                  : `estado desconhecido · ${t!.clientesSemInformacao} ${t!.clientesSemInformacao === 1 ? 'cliente' : 'clientes'} sem contato`
              }
              data-testid="kpi-sem-informacao"
            />
            <KpiCard
              label="Silenciados"
              help={`Pausados de propósito porque há técnico trabalhando no local. Não geram e-mail e não contam contra a disponibilidade.`}
              value={isLoading ? '—' : t!.silenciados}
              context="manutenção programada"
              data-testid="kpi-silenciados"
            />
          </div>

          <p className="text-sm text-ink-3 dark:text-ink-dark-3" data-testid="idade-do-dado">
            “Ao vivo” é o mais recente que chegou, não o instante presente. Cada equipamento é
            testado a cada {data?.config.intervalo_seg ?? 60} segundos e a tela busca o resultado a
            cada 30 — então o estado pode ter cerca de um minuto e meio de idade.{' '}
            {dataUpdatedAt ? `Atualizado ${desdeQuando(new Date(dataUpdatedAt).toISOString(), agora)}.` : ''}
          </p>

          <Card className="space-y-4 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[16rem] flex-1">
                <Label htmlFor="filtro-cliente">Cliente</Label>
                <Select
                  id="filtro-cliente"
                  value={cliente}
                  onChange={(e) => setCliente(e.target.value)}
                  data-testid="filtro-cliente"
                >
                  <option value="">Todos os clientes</option>
                  {clientes.map((c) => (
                    <option key={c.clienteId} value={c.clienteId}>
                      {c.razaoSocial}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="min-w-[16rem] flex-1">
                <Label htmlFor="filtro-busca">Buscar equipamento</Label>
                <Input
                  id="filtro-busca"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Nome ou endereço"
                  data-testid="filtro-busca"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {ESTADOS.map((e) => {
                  const ligado = estados.includes(e.valor);
                  return (
                    <Button
                      key={e.valor}
                      variant={ligado ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() =>
                        setEstados((atual) =>
                          ligado ? atual.filter((x) => x !== e.valor) : [...atual, e.valor],
                        )
                      }
                      data-testid={`filtro-estado-${e.valor}`}
                    >
                      {e.rotulo}
                    </Button>
                  );
                })}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setAgrupar((a) => !a)}>
                {agrupar ? 'Ver lista corrida' : 'Agrupar por cliente'}
              </Button>
            </div>

            {filtrados.length === 0 ? (
              <EmptyState
                title="Nenhum equipamento com esses filtros"
                description="Ajuste o cliente, a busca ou os estados selecionados."
                data-testid="lista-vazia"
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <ThOrdenavel chave="nome" ordem={ordem}>Equipamento</ThOrdenavel>
                      {!agrupar && <ThOrdenavel chave="cliente" ordem={ordem}>Cliente</ThOrdenavel>}
                      <ThOrdenavel chave="endereco" ordem={ordem}>Endereço</ThOrdenavel>
                      <TableHead>Testado por</TableHead>
                      <ThOrdenavel chave="estado" ordem={ordem}>Estado</ThOrdenavel>
                      <ThOrdenavel chave="desde" ordem={ordem}>Neste estado há</ThOrdenavel>
                      <ThOrdenavel chave="teste" ordem={ordem}>Último teste</ThOrdenavel>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agrupar
                      ? filtrados.map((c) => (
                          <LinhasDoCliente
                            key={c.clienteId}
                            cliente={c}
                            ordem={ordem}
                            agora={agora}
                            buscaAtiva={busca.trim().length > 0}
                          />
                        ))
                      : linhasCorridas.map((l) => (
                          <LinhaDeEquipamento
                            key={l.e.id}
                            e={l.e}
                            razaoSocial={l.razaoSocial}
                            agora={agora}
                          />
                        ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * 🔴 Por que o cliente mudo aparece AGRUPADO, e não linha a linha:
 *
 * se a internet de um condomínio caiu, os três equipamentos dele podem estar
 * funcionando perfeitamente. A linha do cliente diz que o coletor perdeu
 * contato; as linhas abaixo ficam como desconhecidas. Tratá-las como quedas
 * produziria "8 equipamentos fora do ar" — um número falso, e é ele que manda
 * a equipe procurar defeito onde não há.
 */
/**
 * 🔴 A linha do cliente EXPANDE E RECOLHE pelo nome (frente 14, 11/09 — o
 * Fortaleza sozinho listava 105 equipamentos inline e a página virava rolagem
 * infinita). A regra que impede o conserto de virar defeito: **cliente com
 * equipamento FORA DO AR ou coletor SEM CONTATO nasce EXPANDIDO** — quem
 * precisa de ação aparece; quem está bem sai do caminho. Os contadores ficam
 * SEMPRE na linha, recolhida ou não. Buscar um equipamento ABRE o cliente
 * dele. O clique do usuário vence a regra; o estado é de sessão (React) —
 * zero localStorage, e nada aqui vale a pena persistir por usuário.
 */
function LinhasDoCliente({
  cliente,
  ordem,
  agora,
  buscaAtiva,
}: {
  cliente: ClienteAoVivo;
  ordem: Ordenacao<ChaveDaOrdem>;
  agora: number;
  buscaAtiva: boolean;
}) {
  const temProblema = cliente.contagem.foraDoAr > 0 || cliente.coletorMudo;
  const [forcada, setForcada] = useState<boolean | null>(null);
  const aberta = forcada ?? (temProblema || buscaAtiva);
  return (
    <>
      <TableRow className="bg-surface-2 dark:bg-surface-dark-2">
        <TableCell colSpan={6} className="font-medium">
          <span className="inline-flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setForcada(!aberta)}
              aria-expanded={aberta}
              className="inline-flex items-center gap-1.5 font-medium text-ink-1 dark:text-ink-dark-1 hover:text-ink-2 dark:hover:text-ink-dark-2 transition-colors"
              data-testid={`alternar-cliente-${cliente.clienteId}`}
            >
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${aberta ? '' : '-rotate-90'}`}
                aria-hidden
              />
              {cliente.razaoSocial}
              <span className="font-normal text-ink-3 dark:text-ink-dark-3">
                ({cliente.equipamentos.length})
              </span>
            </button>
            {cliente.pausado && <Badge variant="warning">Monitoramento pausado</Badge>}
            {cliente.coletorMudo && (
              <Badge variant="neutral" data-testid={`coletor-mudo-${cliente.clienteId}`}>
                Coletor sem contato · última notícia {desdeQuando(cliente.ultimoContatoIso, agora)}
              </Badge>
            )}
            <span className="text-xs font-normal text-ink-3 dark:text-ink-dark-3">
              {cliente.contagem.noAr} no ar · {cliente.contagem.foraDoAr} fora do ar ·{' '}
              {cliente.contagem.semInformacao} sem informação · {cliente.contagem.silenciados}{' '}
              {cliente.contagem.silenciados === 1 ? 'silenciado' : 'silenciados'}
            </span>
          </span>
        </TableCell>
        <TableCell />
      </TableRow>
      {aberta &&
        ordenarLista(cliente.equipamentos, (e) => valorDaOrdem(e, ordem.chave), ordem.sentido).map(
          (e) => <LinhaDeEquipamento key={e.id} e={e} agora={agora} />,
        )}
    </>
  );
}

function LinhaDeEquipamento({
  e,
  razaoSocial,
  agora,
}: {
  e: EquipamentoDaLista;
  /** Presente só na lista corrida — agrupada, o cliente é a linha de cima. */
  razaoSocial?: string;
  agora: number;
}) {
  return (
    <TableRow data-testid={`equipamento-${e.id}`}>
      <TableCell className="font-medium">{e.nome}</TableCell>
      {razaoSocial !== undefined && <TableCell>{razaoSocial}</TableCell>}
      <TableCell className="tabular-nums">{e.endereco}</TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1">
          {e.metodo === 'tcp' ? `TCP ${e.porta ?? ''}`.trim() : 'Ping'}
          {e.metodo === 'tcp' && e.metodoMotivo && <HelpTip texto={e.metodoMotivo} />}
        </span>
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1">
          <SeloDoEstado estado={e.estado} rotulo={e.rotulo} />
          {e.estado === 'silenciado' && e.silenciadoMotivo && <HelpTip texto={e.silenciadoMotivo} />}
        </span>
      </TableCell>
      <TableCell>{e.estado === 'no_ar' ? '—' : desdeQuando(e.desdeIso, agora)}</TableCell>
      <TableCell>{desdeQuando(e.ultimoTesteIso, agora)}</TableCell>
    </TableRow>
  );
}
